/**
 * Settings -> Integrations -> calls coming IN.
 *
 * Its own tab rather than a block under Telephony, because it is a different
 * decision from "can we place calls": everything on this page changes what a
 * customer hears when THEY ring US, and that is not something to discover at the
 * bottom of a credentials form.
 *
 * The one thing that is not here, deliberately, is a webhook URL to paste. An
 * inbound call arrives at the TwiML App's Voice Request URL - the same one
 * outgoing calls already use - so an installation that can dial can receive with
 * nothing but the switch below.
 */
import { useEffect, useState } from "react";
import { PhoneIncoming, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getTelephonySettings,
  saveTelephonySettings,
  type NoticeVariant,
  type TelephonyStatus,
} from "../../../../lib/calls";
import { staffOptionLabel } from "../../../../lib/staff";
import { Btn, StatusPill } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { useStaff } from "../../_shared/staff";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, Pill, SaveRow, SelectPill, ToggleRow } from "../panel/fields";
import { InboundBinding } from "./InboundBinding";
import { NoticeVariantFields } from "./NoticeVariantFields";

const EMPTY: NoticeVariant = { audioUrl: "", text: "" };

export function InboundCard() {
  const t = useT();
  const [status, setStatus] = useState<TelephonyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [enabled, setEnabled] = useState(false);
  const [ringSeconds, setRingSeconds] = useState("25");
  const [blockAnonymous, setBlockAnonymous] = useState(false);
  const [conference, setConference] = useState(false);
  const [voicemail, setVoicemail] = useState(true);
  const [forwardTo, setForwardTo] = useState("");
  const [forwardRingSeconds, setForwardRingSeconds] = useState("20");
  const [mobileOwnerUserId, setMobileOwnerUserId] = useState("");
  const [voicemailPrompt, setVoicemailPrompt] = useState<NoticeVariant>(EMPTY);
  const [unavailablePrompt, setUnavailablePrompt] = useState<NoticeVariant>(EMPTY);
  const [voicemailLead, setVoicemailLead] = useState(true);
  const [voicemailTicket, setVoicemailTicket] = useState(true);

  function hydrate(data: TelephonyStatus) {
    setStatus(data);
    setEnabled(data.inbound.enabled);
    setRingSeconds(String(data.inbound.ringSeconds));
    setBlockAnonymous(data.inbound.blockAnonymous);
    setConference(data.inbound.conference);
    setVoicemail(data.inbound.voicemail);
    setForwardTo(data.inbound.forwardTo);
    setForwardRingSeconds(String(data.inbound.forwardRingSeconds));
    setMobileOwnerUserId(data.inbound.mobileOwnerUserId);
    setVoicemailPrompt({ ...data.inbound.voicemailPrompt });
    setUnavailablePrompt({ ...data.inbound.unavailablePrompt });
    setVoicemailLead(data.inbound.voicemailLead);
    setVoicemailTicket(data.inbound.voicemailTicket);
  }

  // Who could own a call taken on the mobile. The app's one staff directory,
  // which is already loaded if any other screen has asked for it and fails
  // silently if it cannot be: a missing list leaves the owner select holding
  // only "nobody", which is the shipped default anyway - it must not stop the
  // rest of the card from saving.
  const { staff } = useStaff();

  useEffect(() => {
    getTelephonySettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      hydrate(
        await saveTelephonySettings({
          inbound: {
            enabled,
            ringSeconds: Number(ringSeconds),
            blockAnonymous,
            conference,
            voicemail,
            forwardTo,
            forwardRingSeconds: Number(forwardRingSeconds),
            mobileOwnerUserId,
            voicemailPrompt,
            unavailablePrompt,
            voicemailLead,
            voicemailTicket,
          },
        }),
      );
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  // The switch on its own answers nothing: without the Twilio credentials there
  // is no `<Client>` leg to ring, so the phone would be answered with a refusal.
  const notConfigured = enabled && !(status?.twilio.configured ?? false);
  // Typed something that is not a dialable number. Dropped rather than stored,
  // because a dial leg that errors takes the whole `<Dial>` with it - a typo in
  // an optional field would stop the browsers ringing too.
  const forwardDropped = forwardTo.trim().length > 0 && (status?.inbound.forwardTo ?? "") === "";
  // A track to put on, as SAVED: it is uploaded on the Telephony tab and is
  // only real to the phone once it has been saved there.
  const hasHoldMusic = (status?.twilio.holdUrl ?? "").trim() !== "";

  return (
    // The prompt's voice picker is a `Pop` menu, and Settings mounts no
    // `PopHost` of its own - a `Pop` without one is a control that silently does
    // not open.
    <PopHost>
    <IntegrationCard
      icon={PhoneIncoming}
      title={t("settings.inbound.title")}
      desc={t("settings.inbound.desc")}
      status={
        <ConnStatus
          ok={status?.inbound.ready ?? false}
          okLabel={t("settings.inbound.statusOn")}
          offLabel={t("settings.inbound.statusOff")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        <Note tone="info">{t("settings.inbound.about")}</Note>
        {notConfigured ? <Note tone="bad">{t("settings.inbound.needsTwilio")}</Note> : null}
        {forwardDropped ? <Note tone="warn">{t("settings.inbound.forwardDropped")}</Note> : null}

        <ToggleRow
          label={t("settings.inbound.enable")}
          hint={t("settings.inbound.enableHint")}
          checked={enabled}
          onChange={setEnabled}
        />

        {/* The one part of the blocklist that lives in Settings, because it is
            the one part that cannot be a row: a withheld number is not a number
            and cannot be written down as one. The numbers themselves are on the
            operator's own screen, behind a key that is deliberately narrower
            than this page's.

            Off by default and it has to be said yes to on purpose. A withheld
            number is the commonest signature there is on a sales robot, and it
            is also how a hospital, a bank and a person in trouble ring. */}
        <ToggleRow
          label={t("settings.inbound.blockAnonymous")}
          hint={t("settings.inbound.blockAnonymousHint")}
          checked={blockAnonymous}
          onChange={setBlockAnonymous}
        />

        {/* The caller hears our hold music instead of the ringing tone. Only
            offered with a track to play: the server keeps a call with none on
            the ordinary bridge, so a switch that could be turned on without one
            would be a control that visibly does nothing. It stays turnable OFF
            regardless, so a switch saved on before the track was removed can
            still be undone from here. */}
        <ToggleRow
          label={t("settings.inbound.conference")}
          hint={t("settings.inbound.conferenceHint")}
          checked={conference}
          disabled={!hasHoldMusic && !conference}
          onChange={setConference}
        />
        {!hasHoldMusic ? (
          <Note tone={conference ? "warn" : "mut"}>{t("settings.inbound.conferenceNeedsMusic")}</Note>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("settings.inbound.ringSeconds")} hint={t("settings.inbound.ringSecondsHint")}>
            <Pill value={ringSeconds} onChange={setRingSeconds} inputMode="numeric" placeholder="25" />
          </Field>
          <Field label={t("settings.inbound.forwardTo")} hint={t("settings.inbound.forwardToHint")}>
            <Pill
              value={forwardTo}
              onChange={setForwardTo}
              placeholder="+372..."
              autoComplete="off"
              inputMode="tel"
              mono
            />
          </Field>
        </div>

        {/* Only once there is a number to forward to. Two controls describing
            what happens on a stage that cannot run are two more things to read
            past on a page that is already long. */}
        {forwardTo.trim() ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("settings.inbound.forwardRingSeconds")}
              hint={t("settings.inbound.forwardRingSecondsHint")}
            >
              <Pill
                value={forwardRingSeconds}
                onChange={setForwardRingSeconds}
                inputMode="numeric"
                placeholder="20"
              />
            </Field>
            <Field
              label={t("settings.inbound.mobileOwner")}
              hint={t("settings.inbound.mobileOwnerHint")}
            >
              {/* A native select rather than the app's menu: this card is inside
                  a PopHost, but a handful of operators does not need a
                  searchable panel, and the plain control is the one that works
                  identically on a phone. */}
              <SelectPill
                value={mobileOwnerUserId}
                onChange={setMobileOwnerUserId}
                label={t("settings.inbound.mobileOwner")}
              >
                <option value="">{t("settings.inbound.mobileOwnerNone")}</option>
                {/* Deactivated people are last and carry the word: this
                    setting decides whose name a missed mobile call ends up
                    under, and a leaver picked here would file calls to a desk
                    nobody sits at. They stay selectable so an owner set before
                    they left still shows as the saved value. */}
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {staffOptionLabel(s, t("common.inactive"))}
                  </option>
                ))}
              </SelectPill>
            </Field>
          </div>
        ) : null}

        {/* The saved switch, not the one being edited: the check reports what
            the server is running on right now, and an unsaved tick must not
            colour its verdict. */}
        <InboundBinding
          enabled={status?.inbound.enabled ?? false}
          configured={status?.twilio.configured ?? false}
          voiceWebhookUrl={status?.voiceWebhookUrl ?? ""}
        />

        <FieldGroup title={t("settings.inbound.voicemailGroup")}>
          <Note tone="mut">{t("settings.inbound.voicemailHint")}</Note>
          <ToggleRow
            label={t("settings.inbound.voicemail")}
            hint={t("settings.inbound.voicemailToggleHint")}
            checked={voicemail}
            onChange={setVoicemail}
          />
          {/* Only the prompt that would really play. Showing both editors at once
              would put two announcements on screen and no answer about which of
              them the caller hears. */}
          {voicemail ? (
            <NoticeVariantFields
              slot="inbound.voicemail"
              value={voicemailPrompt}
              onChange={setVoicemailPrompt}
              savedClip={status?.clips["inbound.voicemail"]}
              builtInAudio={status?.builtInAudio["inbound.voicemail"] ?? ""}
            />
          ) : (
            <NoticeVariantFields
              slot="inbound.unavailable"
              value={unavailablePrompt}
              onChange={setUnavailablePrompt}
              savedClip={status?.clips["inbound.unavailable"]}
              builtInAudio={status?.builtInAudio["inbound.unavailable"] ?? ""}
            />
          )}
        </FieldGroup>

        {/* Only when there are messages to do something with. With voicemail
            off, these two switches describe a thing that cannot happen, and a
            settings page that offers a choice with no consequence is a page
            people stop trusting. */}
        {voicemail ? (
          <FieldGroup title={t("settings.inbound.intakeGroup")}>
            <Note tone="mut">{t("settings.inbound.intakeHint")}</Note>
            <ToggleRow
              label={t("settings.inbound.voicemailLead")}
              hint={t("settings.inbound.voicemailLeadHint")}
              checked={voicemailLead}
              onChange={setVoicemailLead}
            />
            <ToggleRow
              label={t("settings.inbound.voicemailTicket")}
              hint={t("settings.inbound.voicemailTicketHint")}
              checked={voicemailTicket}
              onChange={setVoicemailTicket}
            />
            {/* Both halves read the transcript, so nothing transcribing means
                no subject to write and no note to file. Asked of the CHOSEN
                provider, not of Gemini: an install on ElevenLabs with no Gemini
                key still transcribes, and the intake still works. Said here
                rather than left to be found by an empty support queue. */}
            {(voicemailLead || voicemailTicket) && status && !status.providers.transcribeReady ? (
              <Note tone="warn">{t("settings.inbound.intakeNeedsTranscript")}</Note>
            ) : null}
            {/* The softer half. There IS a transcript, so a lead and a ticket
                are still opened - they just fall back to the verbatim text
                where the summary would have been, which reads noticeably
                rougher in the queue. */}
            {(voicemailLead || voicemailTicket) &&
            status?.providers.transcribeReady &&
            !status.providers.summaryReady ? (
              <Note tone="mut">{t("settings.inbound.intakeNoSummary")}</Note>
            ) : null}
          </FieldGroup>
        ) : null}

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saveState === "saving" ? t("settings.inbound.saving") : t("settings.inbound.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.inbound.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.inbound.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
    </PopHost>
  );
}
